import React from 'react';
import ServicePageLayout from '../../components/seo/ServicePageLayout';
import { getGallery } from '../../seo/galleryConfig';

const faqs = [
  {
    q: 'My radiator is cold at the top — what does that mean?',
    a: 'Cold at the top usually means there is air trapped in the radiator preventing hot water from filling it completely. Bleeding the radiator (releasing the air with a key on the top corner) normally solves it. If the radiator fills with air repeatedly within days, that points to a deeper system issue we should investigate.'
  },
  {
    q: 'My radiator is cold at the bottom — what does that mean?',
    a: 'Cold at the bottom is the opposite problem: sludge buildup inside the radiator stopping hot water from circulating fully. This usually requires a power-flush of the system or removal and flushing of the specific radiator. Left untreated, sludge eventually fails the radiator entirely.'
  },
  {
    q: 'Should I replace or repair a leaking radiator?',
    a: 'Depends on where the leak is. A leaking radiator valve is usually a £15-£40 part swap and a 30-minute job. A pinhole leak in the radiator panel itself is usually a sign of internal corrosion and replacement is the right call. We will diagnose properly and recommend honestly.'
  },
  {
    q: 'Do you fit smart thermostatic radiator valves (TRVs)?',
    a: 'Yes — we install both standard and smart TRVs (compatible with Hive, Nest, Tado and similar systems). Adding TRVs to a system without them typically pays for itself within 18 months in reduced heating costs.'
  },
  {
    q: 'How often should radiators be bled?',
    a: 'Most systems benefit from bleeding once a year, at the start of the heating season. If you find yourself bleeding the same radiator more than twice a year, something else is going on — call us.'
  },
];

const relatedServices = [
  { slug: 'plumbing', label: 'Plumbing Services', blurb: 'Full domestic plumbing repairs and upgrades.' },
  { slug: 'emergency-plumber', label: 'Emergency Plumber', blurb: 'No heat in winter? Call our 24/7 emergency line.' },
  { slug: 'leak-detection-repairs', label: 'Leak Detection', blurb: 'When the radiator drip is part of something larger.' },
];

const RadiatorRepairsPage = () => (
  <ServicePageLayout
    seoTitle="Radiator Repairs & Installation in Kent, London & Essex | H.A.R.P Solutions LTD"
    seoDescription="Radiator repairs, replacements, and installations across Kent, London and Essex. Cold radiators, leaks, TRV upgrades, system power-flushing. Call +44 330 122 1094."
    canonicalPath="/services/radiator-repairs"
    keywords="radiator repairs, radiator installation, cold radiator, heating repairs, radiator valve replacement, radiator repair Kent, radiator repair London, radiator repair Essex"
    pageTitle="Radiator Repairs, Replacements & Smart Valve Installations"
    serviceName="Radiator Repairs"
    serviceSlug="radiator-repairs"
    heroSubheading="Warm, balanced, efficient heating across every room of your home."
    intro="Cold radiators waste money and leave your home uncomfortable. A radiator that leaks even slightly will eventually rust through floorboards or carpet underlay. H.A.R.P Solutions diagnoses and fixes every type of radiator and heating-system issue across Kent, London and Essex — from a 20-minute bleed and balance to full system power-flushes and modern smart-TRV installations."
    bullets={[
      'Cold radiator diagnosis — top, middle, or bottom',
      'Bleeding, balancing and system pressurising',
      'Leaking valve and pinhole leak repair',
      'Like-for-like and upgrade radiator replacement',
      'Smart TRV installation (Hive, Nest, Tado)',
      'Full system power-flushing to remove sludge',
    ]}
    faqs={faqs}
    relatedServices={relatedServices}
    galleryImages={getGallery('radiator-repairs')}
  >
    <h2>Diagnosing Cold Radiator Problems</h2>
    <p>"Cold" radiators are not all the same problem — the location of the cold spot tells us what is wrong.</p>
    <h3>Cold at the top</h3>
    <p>
      Air trapped at the top displacing the hot water. Bleeding releases the air. If it recurs within days, the system is drawing air in
      somewhere — often a failing automatic air vent, micro-leak, or expansion-vessel issue.
    </p>
    <h3>Cold in the middle, hot at the edges</h3>
    <p>
      Sludge buildup inside the radiator restricting flow. Power-flushing the system clears it in most cases. For badly affected
      radiators, removal and flushing off-site is faster.
    </p>
    <h3>Cold at the bottom</h3>
    <p>
      Heavy sludge accumulation OR a system balance issue (the rest of the system is taking all the hot water before it reaches this
      radiator). We measure flow temperatures across the system to identify which.
    </p>
    <h3>Completely cold</h3>
    <p>
      The flow valve is closed, the TRV head has seized in the closed position, or the radiator has been isolated and forgotten. Quick to
      diagnose, quick to fix.
    </p>

    <h2>Radiator Leaks</h2>
    <p>Three places a radiator typically leaks:</p>
    <ul>
      <li><strong>Bleed nipple at the top.</strong> Either failed gasket or simply wasn't closed properly after bleeding. Trivial repair.</li>
      <li><strong>Valve at the bottom.</strong> Replace the valve and union — straightforward 30-minute job.</li>
      <li><strong>The radiator panel itself.</strong> Internal corrosion has reached pinhole-through stage. Replacement is the correct fix; trying to repair a corroded panel is a short-term patch at best.</li>
    </ul>

    <h2>Replacements &amp; Upgrades</h2>
    <p>
      Replacing an old radiator is more than just like-for-like. Modern designs (vertical column, flat panel, designer finishes) can free
      up wall space and increase a room's effective heat output. We discuss what you actually need rather than just selling the largest
      unit that fits.
    </p>
    <p>
      Smart TRV installation is one of the most cost-effective heating upgrades we install. By heating only the rooms in active use,
      most households see a 12-20% reduction in heating bills within a year.
    </p>

    <h2>Power-Flushing</h2>
    <p>
      If your system has multiple sludged radiators, a single boiler pressure issue, or hasn't been serviced in 10+ years, a
      power-flush often restores it to near-new performance. We pump cleaning chemicals through the system at high flow to clear
      decades of accumulated debris. The boiler runs hotter, radiators heat more evenly, and the next 5+ years of efficiency improves
      significantly.
    </p>

    <h2>Book A Visit</h2>
    <p>
      Call <a href="tel:+443301221094"><strong>+44 330 122 1094</strong></a> for a quick chat — most radiator issues can be priced over the
      phone. For 24/7 no-heat emergencies see our <a href="/services/emergency-plumber">emergency plumber service</a>.
    </p>
  </ServicePageLayout>
);

export default RadiatorRepairsPage;
